import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import {
  createTeamCategory,
  setTeamCategoryMembers,
  updateTeamCategory,
  type TeamCategory,
} from '@/api/teamCategories';
import { listTeams, type Team } from '@/api/teams';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormErrorCallout } from '@/components/FormErrorCallout';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CALLING_LEAD_VALUES, leadFromSelect, leadOptionKey, leadToSelect } from '@/lib/callingLead';
import { cn } from '@/lib/utils';

// The "Kategori" tab of the team-category side-drawer: name, description,
// default calling lead-time, and team assignment. Extracted verbatim from
// the former TeamCategoryModal so the drawer can host it alongside the
// scheduling-rules + notices tabs. Owns its own save (the tabs persist
// independently, mirroring the per-section save on the member drawer).
//
// `onSaved` is called after a successful create so the parent can swap the
// URL from create-mode to ?edit=<newId> and reveal the other tabs.

export interface CategoryBasicsTabProps {
  slug: string;
  category?: TeamCategory | undefined;
  open: boolean;
  onClose: () => void;
  onSaved?: (saved: TeamCategory) => void;
}

export function CategoryBasicsTab({ slug, category, open, onClose, onSaved }: CategoryBasicsTabProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [callingLead, setCallingLead] = useState<string>(leadToSelect(7));
  const [selectedTeams, setSelectedTeams] = useState<Set<string>>(new Set());
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorTick, setErrorTick] = useState(0);

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug && open,
  });
  const teams = useMemo<Team[]>(() => teamsQuery.data?.teams ?? [], [teamsQuery.data]);

  // Seed on open-edge (once per open), reading whatever teams are cached.
  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setDescription(category?.description ?? '');
    setCallingLead(leadToSelect(category?.calling_lead_days ?? 7));
    const initial = new Set<string>();
    if (category && teamsQuery.data) {
      for (const tm of teamsQuery.data.teams) {
        if (tm.category_id === category.id) initial.add(tm.id);
      }
    }
    setSelectedTeams(initial);
    setErrorCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category]);

  // Retro-seed team checkboxes when the teams query lands after open.
  useEffect(() => {
    if (!open || !category || !teamsQuery.data) return;
    setSelectedTeams((prev) => {
      if (prev.size > 0) return prev;
      const seeded = new Set<string>();
      for (const tm of teamsQuery.data!.teams) {
        if (tm.category_id === category.id) seeded.add(tm.id);
      }
      return seeded;
    });
  }, [open, category, teamsQuery.data]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        calling_lead_days: leadFromSelect(callingLead, false) ?? -1,
      };
      const saved = category
        ? await updateTeamCategory(token, slug, category.id, payload)
        : await createTeamCategory(token, slug, payload);
      const wantedIds = Array.from(selectedTeams);
      if (category) {
        const currentIds = new Set(
          teams.filter((tm) => tm.category_id === category.id).map((tm) => tm.id),
        );
        const sameSet =
          currentIds.size === selectedTeams.size &&
          Array.from(selectedTeams).every((id) => currentIds.has(id));
        if (!sameSet) await setTeamCategoryMembers(token, slug, saved.id, wantedIds);
      } else if (wantedIds.length > 0) {
        await setTeamCategoryMembers(token, slug, saved.id, wantedIds);
      }
      return saved;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['team-categories', slug] });
      void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
      if (!category) onSaved?.(saved);
      else onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
      setErrorTick((n) => n + 1);
    },
  });

  const canSubmit = name.trim().length > 0 && !mutation.isPending;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorCode(null);
    mutation.mutate();
  };

  const submitKey = category
    ? 'settings.teamCategories.modal.save'
    : 'settings.teamCategories.modal.create';
  const submittingKey = category
    ? 'settings.teamCategories.modal.saving'
    : 'settings.teamCategories.modal.creating';

  return (
    <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-6">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('settings.teamCategories.modal.name')}
          <Input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrorCode(null);
            }}
            required
            maxLength={80}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('settings.teamCategories.modal.namePlaceholder')}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('settings.teamCategories.modal.description')}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={3}
            className={cn(
              'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
              'ring-offset-background placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            )}
            placeholder={t('settings.teamCategories.modal.descriptionPlaceholder')}
          />
        </label>

        <label className="flex flex-col gap-1.5 rounded-xl border border-border bg-muted/20 p-3 text-sm font-medium text-foreground">
          {t('settings.teamCategories.sendCallings.label')}
          <Select value={callingLead} onValueChange={setCallingLead}>
            <SelectTrigger className="min-h-[var(--tap)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALLING_LEAD_VALUES.map((v) => (
                <SelectItem key={v} value={leadToSelect(v)}>
                  {t(leadOptionKey(v))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs font-normal text-muted-foreground">
            {t('settings.teamCategories.sendCallings.help')}
          </p>
        </label>

        <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
          <legend className="px-1 text-sm font-medium text-foreground">
            {t('settings.teamCategories.modal.teams')}
          </legend>
          <p className="text-xs text-muted-foreground">
            {t('settings.teamCategories.modal.teamsHelp')}
          </p>
          {teamsQuery.isPending ? (
            <p className="text-xs text-muted-foreground">{t('teams.loading')}</p>
          ) : teams.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t('settings.teamCategories.modal.teamsEmpty')}
            </p>
          ) : (
            <div className="flex max-h-56 flex-col gap-1 overflow-y-auto pr-1">
              {teams.map((tm) => {
                const checked = selectedTeams.has(tm.id);
                const otherCategory =
                  tm.category_id && tm.category_name && tm.category_id !== category?.id
                    ? tm.category_name
                    : '';
                return (
                  <label
                    key={tm.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-background"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        setSelectedTeams((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(tm.id);
                          else next.delete(tm.id);
                          return next;
                        });
                      }}
                    />
                    <span className="flex-1 truncate">{tm.name}</span>
                    {otherCategory && (
                      <span
                        className="shrink-0 rounded-pill border border-border bg-background px-2 py-0.5 text-[0.7rem] font-medium text-muted-foreground"
                        title={t('settings.teamCategories.modal.teamCurrentlyIn', { name: otherCategory })}
                      >
                        {otherCategory}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        {errorCode && (
          <FormErrorCallout key={errorTick} heading={t('settings.teamCategories.modal.error.heading')}>
            <p>
              {t(`settings.teamCategories.modal.error.${errorCode}`, {
                defaultValue: t('settings.teamCategories.modal.error.unknown'),
              })}
            </p>
          </FormErrorCallout>
        )}
      </div>

      <footer className="flex justify-end gap-2 border-t border-border bg-background p-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {mutation.isPending ? t(submittingKey) : t(submitKey)}
        </Button>
      </footer>
    </form>
  );
}
